/**
 * RepCoachingPanel — Rep-facing coaching focus surface (LaneR, 2026-05-28).
 *
 * Displays coaching signals scoped to one rep. Pulls from:
 *   (a) coaching_notes WHERE rep_user_id = repUserId AND visibility = 'rep_visible'
 *       — manager-authored notes visible to this rep
 *   (b) Fallback signals derived from live Supabase data:
 *       - Accounts untouched 14+ days
 *       - Accounts below 5X pipeline coverage (AE only)
 *       - Accounts with red CP status on CP1-CP5
 *
 * View-only. No reply UI. Title: "Coaching focus".
 * Tenant 1 only. Canon: no emoji, no exclamation, M-PACT hyphenated, Checkpoint.
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/session";
import { tokens } from "@/components/operator/tokens";

// ─── Types ──────────────────────────────────────────────────────────────────

interface CoachingNote {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null;
  account_name?: string | null;
  account_id?: number | null;
}

interface SignalItem {
  account_id: number;
  account_name: string;
  signal_type: "untouched_14d" | "below_5x" | "red_cp";
  badge: string;
  recommended_action: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  repUserId: string;
  repRole: "bdr" | "ae";
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PanelHeader({ count }: { count: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderBottom: `1px solid ${tokens.color.border}`,
        background: tokens.color.surfaceMute,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: tokens.font.body,
            fontWeight: 700,
            fontSize: 12,
            color: tokens.color.textPrimary,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Coaching focus
        </div>
        <div style={{ fontFamily: tokens.font.body, fontSize: 11, color: tokens.color.textMuted, marginTop: 1 }}>
          From your manager
        </div>
      </div>
      {count > 0 && (
        <span
          style={{
            background: "#FEE2E2",
            color: "#9A1F1F",
            border: "1px solid #FCA5A5",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            fontFamily: tokens.font.body,
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function CoachingRow({
  primary,
  secondary,
  badge,
  badgeColor,
  onClick,
}: {
  primary: string;
  secondary?: string;
  badge?: string;
  badgeColor?: "red" | "amber";
  onClick?: () => void;
}) {
  const badgeBg = badgeColor === "amber" ? "#FEF3C7" : "#FEE2E2";
  const badgeBorder = badgeColor === "amber" ? "#FCD34D" : "#FCA5A5";
  const badgeText = badgeColor === "amber" ? "#92400E" : "#9A1F1F";

  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "9px 14px",
        borderTop: `1px solid ${tokens.color.borderSoft}`,
        cursor: onClick ? "pointer" : "default",
        fontFamily: tokens.font.body,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: tokens.color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {primary}
        </div>
        {secondary && (
          <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {secondary}
          </div>
        )}
      </div>
      {badge && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: badgeText,
            background: badgeBg,
            border: `1px solid ${badgeBorder}`,
            borderRadius: 4,
            padding: "2px 7px",
            whiteSpace: "nowrap",
            marginLeft: 10,
            flexShrink: 0,
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ padding: "12px 14px", fontSize: 12, color: tokens.color.textMuted, fontFamily: tokens.font.body }}>
      {message}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function RepCoachingPanel({ repUserId, repRole }: Props) {
  const [, setLocation] = useLocation();

  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<CoachingNote[]>([]);
  const [signals, setSignals] = useState<SignalItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();

        // (a) Manager coaching notes visible to this rep
        const { data: noteData } = await sb
          .from("coaching_notes")
          .select("id, body, created_at, manager_user_id, rep_user_id")
          .eq("rep_user_id", repUserId)
          .eq("visibility", "rep_visible")
          .order("created_at", { ascending: false })
          .limit(10);

        const noteRows = (noteData ?? []) as any[];

        // Enrich with manager names
        const mgIds = Array.from(new Set(noteRows.map((n) => n.manager_user_id).filter(Boolean)));
        let mgNameMap = new Map<string, string>();
        if (mgIds.length > 0) {
          const { data: mgUsers } = await sb
            .from("users")
            .select("id, full_name")
            .in("id", mgIds);
          mgNameMap = new Map((mgUsers ?? []).map((u: any) => [u.id, u.full_name]));
        }

        const mappedNotes: CoachingNote[] = noteRows.map((n) => ({
          id: n.id,
          body: n.body,
          created_at: n.created_at,
          author_name: mgNameMap.get(n.manager_user_id) ?? "Manager",
          account_id: null,
          account_name: null,
        }));

        // (b) Fallback signals: accounts owned by this rep
        const { data: acctData } = await sb
          .from("accounts")
          .select("id, name, updated_at, est_arr")
          .eq("owner_user_id", repUserId)
          .order("name", { ascending: true });

        const accts = (acctData ?? []) as any[];
        const acctIds = accts.map((a) => a.id);

        // CP1-CP5 status for red signal
        let cpData: any[] = [];
        if (acctIds.length > 0) {
          const { data: cps } = await sb
            .from("account_cp_status")
            .select("account_id, cp_number, status")
            .in("account_id", acctIds)
            .in("cp_number", [1, 2, 3, 4, 5]);
          cpData = cps ?? [];
        }

        // Pipeline summary (AE only — for 5X coverage signal)
        let pipeMap = new Map<number, number>();
        if (repRole === "ae" && acctIds.length > 0) {
          const { data: planData } = await sb
            .from("account_plans")
            .select("account_id, weighted_pipeline, annual_quota")
            .eq("owner_user_id", repUserId);
          for (const p of planData ?? []) {
            pipeMap.set(p.account_id, Number(p.weighted_pipeline ?? 0));
          }
        }

        // Build signal list
        const signalList: SignalItem[] = [];

        // Untouched 14+ days
        for (const a of accts) {
          const age = daysSince(a.updated_at);
          if (age >= 14) {
            signalList.push({
              account_id: a.id,
              account_name: a.name,
              signal_type: "untouched_14d",
              badge: `${age}d idle`,
              recommended_action: "Log a touch or update account status.",
            });
          }
        }

        // Red CP on CP1-CP5
        const cpByAccount = new Map<number, any[]>();
        for (const c of cpData) {
          if (!cpByAccount.has(c.account_id)) cpByAccount.set(c.account_id, []);
          cpByAccount.get(c.account_id)!.push(c);
        }
        for (const a of accts) {
          const cps = cpByAccount.get(a.id) ?? [];
          const redCps = cps.filter((c) => c.status === "red" || c.status === "blocked");
          if (redCps.length > 0 && !signalList.some((s) => s.account_id === a.id && s.signal_type === "untouched_14d")) {
            const cpNums = redCps.map((c) => `CP${c.cp_number}`).join(", ");
            signalList.push({
              account_id: a.id,
              account_name: a.name,
              signal_type: "red_cp",
              badge: `${cpNums} blocked`,
              recommended_action: `Advance ${cpNums} — clear the blocking signal.`,
            });
          }
        }

        // Below 5X coverage (AE only)
        if (repRole === "ae") {
          for (const a of accts) {
            const pipe = pipeMap.get(a.id) ?? 0;
            const arr = Number(a.est_arr ?? 0);
            if (arr > 0 && pipe / arr < 5 && !signalList.some((s) => s.account_id === a.id)) {
              signalList.push({
                account_id: a.id,
                account_name: a.name,
                signal_type: "below_5x",
                badge: `${(pipe / arr).toFixed(1)}x coverage`,
                recommended_action: "Build pipeline to reach 5X coverage on this account.",
              });
            }
          }
        }

        // Rank: red_cp > untouched_14d > below_5x — take top 5
        const ranked = [...signalList].sort((a, b) => {
          const order = { red_cp: 0, untouched_14d: 1, below_5x: 2 };
          return order[a.signal_type] - order[b.signal_type];
        }).slice(0, 5);

        if (cancelled) return;
        setNotes(mappedNotes);
        setSignals(ranked);
      } catch {
        // Non-fatal — renders as empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [repUserId, repRole]);

  const totalCount = notes.length + signals.length;

  if (loading) {
    return (
      <div
        style={{
          border: `1px solid ${tokens.color.border}`,
          borderRadius: 10,
          background: tokens.color.canvas,
          marginBottom: 14,
          padding: "12px 14px",
          fontSize: 12,
          color: tokens.color.textMuted,
          fontFamily: tokens.font.body,
        }}
      >
        Loading coaching focus...
      </div>
    );
  }

  // If nothing to show, render an explicit empty state (Bryan requirement: do NOT hide)
  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 10,
        background: tokens.color.canvas,
        overflow: "hidden",
        marginBottom: 14,
      }}
    >
      <PanelHeader count={totalCount} />

      {totalCount === 0 ? (
        <EmptyState message="No coaching flags for your accounts. All clear." />
      ) : (
        <>
          {/* Manager notes first */}
          {notes.map((note) => (
            <CoachingRow
              key={note.id}
              primary={note.body.length > 80 ? note.body.slice(0, 80) + "…" : note.body}
              secondary={`${note.author_name ?? "Manager"} · ${fmtDate(note.created_at)}`}
              badge="Manager note"
              badgeColor="amber"
              onClick={note.account_id ? () => setLocation(`/account/${note.account_id}`) : undefined}
            />
          ))}

          {/* Derived signals */}
          {signals.map((sig) => (
            <CoachingRow
              key={`${sig.signal_type}-${sig.account_id}`}
              primary={sig.account_name}
              secondary={sig.recommended_action}
              badge={sig.badge}
              badgeColor="red"
              onClick={() => setLocation(`/account/${sig.account_id}`)}
            />
          ))}
        </>
      )}
    </div>
  );
}
